<template>
  <div>
    <CButton color="link" class="px-0 mb-2" :to="{ path: '/admins/tenantList' }">
      <CIcon name="cil-arrow-left" /> Tenants
    </CButton>
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <h1 class="h4 mb-1 font-weight-bold">
          {{ isNewApplication() ? "New tenant" : obj.tenantAdmin.fullName || obj.tenantAdmin.email || "Tenant" }}
        </h1>
        <p class="text-muted mb-0">
          {{
            isNewApplication()
              ? "Create a sign-in for a new organisation"
              : obj.tenantAdmin.email
          }}
        </p>
      </CCardBody>
    </CCard>

    <CCard v-if="loadError">
      <CCardBody class="text-center p-5">
        <CIcon name="cil-warning" size="xl" class="text-danger mb-3" />
        <h2 class="h6 font-weight-bold">Couldn't load this tenant</h2>
        <p class="text-muted">Check your connection and try again.</p>
        <CButton color="primary" @click="resetObj">Retry</CButton>
      </CCardBody>
    </CCard>

    <CCard v-else>
      <CCardBody>
        <CTabs add-tab-classes="mt-1">
          <CTab active>
            <template slot="title">
              <CIcon name="cil-calculator" /> General
            </template>
            <div class="pt-3">
              <CForm @submit.prevent="submit">
                <CInput
                  label="Email *"
                  type="email"
                  horizontal
                  v-model="obj.tenantAdmin.email"
                  :readonly="!isNewApplication()"
                />
                <CInput
                  v-if="!isNewApplication()"
                  label="Full name"
                  horizontal
                  v-model="obj.tenantAdmin.fullName"
                />
                <CRow form class="form-group" v-if="!isNewApplication()">
                  <CCol tag="label" sm="3" class="col-form-label"> Set a new password </CCol>
                  <CCol sm="9">
                    <CSwitch class="mr-1" color="primary" :checked.sync="isResetPassword" />
                  </CCol>
                </CRow>
                <CInput
                  v-if="isResetPassword"
                  label="Password *"
                  type="password"
                  horizontal
                  autocomplete="new-password"
                  v-model="plainPassword"
                />
                <CInput
                  v-if="isResetPassword"
                  label="Confirm password *"
                  type="password"
                  horizontal
                  autocomplete="new-password"
                  v-model="passwordConfirmation"
                />
                <CRow form class="form-group" v-if="!isNewApplication()">
                  <CCol tag="label" sm="3" class="col-form-label"> Can sign in </CCol>
                  <CCol sm="9">
                    <CSwitch class="mr-1" color="primary" :checked.sync="obj.isEnable" />
                  </CCol>
                </CRow>
              </CForm>
              <div class="d-flex justify-content-between border-top pt-3">
                <CButton color="light" :disabled="saving" :to="{ path: '/admins/tenantList' }">
                  Cancel
                </CButton>
                <CButton color="primary" :disabled="saving" @click="submit">
                  {{ isNewApplication() ? "Add tenant" : "Save" }}
                </CButton>
              </div>
            </div>
          </CTab>

          <CTab v-if="!isNewApplication()">
            <template slot="title">
              <CIcon name="cil-basket" /> Subscriptions
            </template>
            <div class="pt-3">
              <div class="d-flex justify-content-end mb-3">
                <CButton color="primary" @click="addNewSubscription">
                  <CIcon name="cil-plus" /> Add subscription
                </CButton>
              </div>
              <div v-if="!computedSubscriptionHistory.length" class="text-center p-4">
                <p class="text-muted mb-0">No subscriptions yet</p>
              </div>
              <template v-else>
                <div class="d-none d-lg-block">
                  <CDataTable
                    :items="computedSubscriptionHistory"
                    :fields="subsriptionFields"
                    items-per-page-select
                    :items-per-page="25"
                    hover
                    sorter
                    pagination
                    :loading="subscrptionLoading"
                  >
                    <template #isActiveYesNo="{ item }">
                      <td>
                        <CBadge :color="item.isActive ? 'success' : 'secondary'">
                          {{ item.isActive ? "Active" : "Inactive" }}
                        </CBadge>
                      </td>
                    </template>
                    <template #amount="{ item }">
                      <td class="text-right">RM {{ formatMyr(item.amount) }}</td>
                    </template>
                    <template #actions="{ item }">
                      <td class="text-nowrap">
                        <CButton
                          color="light"
                          size="sm"
                          class="mr-1"
                          :aria-label="`Edit subscription ${item.packageName}`"
                          @click="onEditSubscription(item)"
                        >
                          <CIcon name="cil-pencil" />
                        </CButton>
                        <CButton
                          color="light"
                          size="sm"
                          :aria-label="`Delete subscription ${item.packageName}`"
                          @click="showDeleteSubscription(item)"
                        >
                          <CIcon name="cil-trash" class="text-danger" />
                        </CButton>
                      </td>
                    </template>
                  </CDataTable>
                </div>
                <div class="d-lg-none">
                  <div
                    v-for="item in computedSubscriptionHistory"
                    :key="item.id"
                    class="border rounded p-3 mb-2"
                  >
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <div class="font-weight-bold">{{ item.packageName }}</div>
                        <div class="small text-muted">
                          {{ item.startDate }} – {{ item.endDate }} · RM {{ formatMyr(item.amount) }}
                        </div>
                      </div>
                      <CBadge :color="item.isActive ? 'success' : 'secondary'">
                        {{ item.isActive ? "Active" : "Inactive" }}
                      </CBadge>
                    </div>
                    <div class="mt-2">
                      <CButton color="light" size="sm" class="mr-2" @click="onEditSubscription(item)">
                        <CIcon name="cil-pencil" /> Edit
                      </CButton>
                      <CButton color="light" size="sm" @click="showDeleteSubscription(item)">
                        <CIcon name="cil-trash" class="text-danger" /> Delete
                      </CButton>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </CTab>

          <CTab v-if="!isNewApplication()">
            <template slot="title">
              <CIcon name="cil-building" /> Businesses
            </template>
            <div class="pt-3">
              <div v-if="!(obj.businesses || []).length" class="text-center p-4">
                <p class="text-muted mb-0">This tenant has no businesses yet</p>
              </div>
              <ul v-else class="list-unstyled mb-0">
                <li v-for="b in obj.businesses" :key="b.id" class="border rounded p-3 mb-2">
                  {{ b.name }}
                </li>
              </ul>
            </div>
          </CTab>

          <CTab v-if="!isNewApplication()">
            <template slot="title">
              <CIcon name="cil-people" /> Users
            </template>
            <div class="pt-3">
              <div v-if="!computedProfileItems.length" class="text-center p-4">
                <p class="text-muted mb-0">This tenant has no users yet</p>
              </div>
              <template v-else>
                <div class="d-none d-lg-block">
                  <CDataTable
                    :items="computedProfileItems"
                    :fields="profilefields"
                    items-per-page-select
                    :items-per-page="25"
                    hover
                    sorter
                    pagination
                  />
                </div>
                <div class="d-lg-none">
                  <div
                    v-for="p in computedProfileItems"
                    :key="p.id"
                    class="border rounded p-3 mb-2"
                  >
                    <div class="font-weight-bold">{{ p.fullName }}</div>
                    <div class="small text-muted">{{ p.email }} · {{ p.role }}</div>
                  </div>
                </div>
              </template>
            </div>
          </CTab>
        </CTabs>
      </CCardBody>
    </CCard>

    <!-- Subscription form -->
    <CModal
      :show.sync="editSubsriptionModal"
      :close-on-backdrop="false"
      :centered="true"
      size="lg"
    >
      <template #header>
        <h2 class="h6 modal-title font-weight-bold mb-0">
          {{ subscription.id ? "Edit subscription" : "Add subscription" }}
        </h2>
        <CButtonClose aria-label="Close" @click="editSubsriptionModal = false" />
      </template>
      <CForm @submit.prevent="updateSubscription">
        <CRow form class="form-group">
          <CCol tag="label" sm="3" class="col-form-label"> Package * </CCol>
          <CCol sm="9">
            <v-select
              class="w-100"
              v-model="selectedPackage"
              :label="'name'"
              :options="packageItems"
              placeholder="Select a package"
            />
          </CCol>
        </CRow>
        <CInput horizontal label="Start date *" type="date" v-model="subscriptionStartDate" />
        <CInput horizontal label="End date *" type="date" v-model="subscriptionEndDate" />
        <CRow form class="form-group">
          <CCol tag="label" sm="3" class="col-form-label"> Active </CCol>
          <CCol sm="9">
            <CSwitch class="mr-1" color="primary" :checked.sync="subscription.isActive" />
          </CCol>
        </CRow>
        <CInput label="Amount" horizontal prepend="RM" inputmode="decimal" v-model="subscription.amount" />
        <CInput label="Unit" horizontal v-model="subscription.unit" />
        <CInput label="Currency" horizontal v-model="subscription.currency" />
        <CInput label="Payment method" horizontal v-model="subscription.paymentMethod" />
        <CInput label="Referral code used" horizontal v-model="subscription.referralCodeUsed" />
        <CSelect
          horizontal
          :value.sync="selectedSubscriptionStatus"
          :options="subscriptionStatuses"
          label="Status"
        />
      </CForm>
      <template #footer>
        <CButton color="light" :disabled="savingSubscription" @click="editSubsriptionModal = false">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="savingSubscription || !selectedPackage" @click="updateSubscription">
          {{ subscription.id ? "Save" : "Add subscription" }}
        </CButton>
      </template>
    </CModal>

    <!-- Delete subscription confirm -->
    <CModal
      :title="`Delete the ${subscriptionToDelete.packageName || ''} subscription?`"
      :show.sync="deleteSubscriptionModal"
      :close-on-backdrop="!deletingSubscription"
    >
      The subscription from {{ subscriptionToDelete.startDate }} to
      {{ subscriptionToDelete.endDate }} will be removed from this tenant. This can't be undone.
      <template #footer>
        <CButton color="light" :disabled="deletingSubscription" @click="deleteSubscriptionModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deletingSubscription" @click="doDeleteSubscription">
          Delete subscription
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import SubscriptionApi from "../../lib/subscriptionApi";
import TenantApi from "../../lib/tenantApi";
import PackageApi from "@/lib/packageApi";
import { formatDate, asDateString, mytToday, toApiInstant } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const subsriptionFields = [
  { key: "startDate", label: "Start date" },
  { key: "endDate", label: "End date" },
  { key: "packageName", label: "Plan" },
  { key: "isActiveYesNo", label: "Active" },
  { key: "statusDescription", label: "Status" },
  { key: "amount", label: "Amount", _classes: "text-right" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

const profilefields = [
  { key: "fullName", label: "Full name" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
];

/** API instant or Date -> "yyyy-MM-dd" in Malaysia time, for <input type="date">. */
function toInputDate(value) {
  if (!value) return mytToday();
  return asDateString(value instanceof Date ? value : new Date(value)) || mytToday();
}

export default {
  name: "Tenant",
  components: {
    vSelect,
  },
  data: () => {
    return {
      isResetPassword: false,
      plainPassword: "",
      passwordConfirmation: "",
      saving: false,
      loadError: false,
      subscrptionLoading: false,
      subscriptionApi: new SubscriptionApi(),
      selectedSubscriptionStatus: 0,
      subscriptionStatuses: [],
      packageApi: new PackageApi(),
      selectedPackage: null,
      packageItems: [],

      subscriptionStartDate: mytToday(),
      subscriptionEndDate: mytToday(),
      subscription: {},
      editSubsriptionModal: false,
      savingSubscription: false,
      deleteSubscriptionModal: false,
      deletingSubscription: false,
      subscriptionToDelete: {},
      subsriptionFields,
      profilefields,

      obj: {
        name: "",
        tenantAdmin: {
          fullName: "",
          email: "",
        },
        subscriptions: [],
      },
      api: new TenantApi(),
    };
  },
  mounted() {
    this.refreshPackages();
    this.fetchSubscriptionStatus();
    this.resetObj();
  },

  computed: {
    computedSubscriptionHistory() {
      return (this.obj.subscriptions || []).map((item) => {
        return {
          ...item,
          rawStartDate: item.startDate,
          rawEndDate: item.endDate,
          startDate: formatDate(item.startDate),
          endDate: formatDate(item.endDate),
          isActiveYesNo: item.isActive ? "Active" : "Inactive",
          packageName: item.package ? item.package.name : "",
        };
      });
    },
    computedProfileItems() {
      return (this.obj.profiles || []).map((item) => {
        const role = item.appUser ? item.appUser.role : "";
        return {
          ...item,
          role: role ? role.charAt(0).toUpperCase() + role.slice(1) : "",
        };
      });
    },
  },

  methods: {
    formatMyr,
    isNewApplication() {
      return !this.$route.params.id;
    },

    updateSubscription() {
      if (!this.selectedPackage || this.savingSubscription) return;
      this.subscription.packageId = this.selectedPackage.id;
      this.subscription.status = this.selectedSubscriptionStatus;
      this.subscription.tenantId = this.obj.id;
      this.subscription.startDate = toApiInstant(new Date(this.subscriptionStartDate));
      this.subscription.endDate = toApiInstant(new Date(this.subscriptionEndDate));
      this.addOrUpdateSubscription(this.subscription);
    },
    addOrUpdateSubscription(editObj) {
      var self = this;
      const isNew = !editObj.id;
      self.savingSubscription = true;
      const request = isNew
        ? self.subscriptionApi.create(editObj)
        : self.subscriptionApi.update(editObj);
      request
        .then(() => {
          self.$toast.success(isNew ? "Subscription added" : "Subscription saved");
          self.resetObj();
          self.editSubsriptionModal = false;
        })
        .catch(({ data }) => {
          self.$toast.error("Couldn't save the subscription. " + helper.getErrorMessage(data));
        })
        .finally(() => {
          self.savingSubscription = false;
        });
    },

    fetchSubscriptionStatus() {
      var self = this;
      self.subscriptionApi
        .getSubscriptionStatus()
        .then((response) => {
          self.subscriptionStatuses = (response.result || []).map((state) => ({
            value: state.id,
            label: state.name,
          }));
        })
        .catch((error) => {
          console.error(error);
        });
    },

    refreshPackages() {
      var self = this;
      self.packageApi
        .getListByActive()
        .then((response) => {
          self.packageItems = response.result || [];
        })
        .catch((error) => {
          console.error(error);
        });
    },

    addNewSubscription() {
      this.selectedPackage = this.packageItems[0] || null;
      this.subscriptionStartDate = mytToday();
      this.subscriptionEndDate = mytToday();
      this.subscription = {
        isActive: true,
        amount: 0,
        unit: "",
        currency: "RM",
        paymentMethod: "N/A",
        referralCodeUsed: "",
      };
      this.editSubsriptionModal = true;
    },
    onEditSubscription(item) {
      const source = (this.obj.subscriptions || []).find((s) => s.id === item.id) || item;
      this.subscription = { ...source };
      delete this.subscription.package;
      this.selectedPackage =
        this.packageItems.find((p) => p.id === source.packageId) || source.package || null;
      this.selectedSubscriptionStatus = source.status;
      this.subscriptionStartDate = toInputDate(source.startDate);
      this.subscriptionEndDate = toInputDate(source.endDate);
      this.editSubsriptionModal = true;
    },
    showDeleteSubscription(item) {
      this.subscriptionToDelete = item;
      this.deleteSubscriptionModal = true;
    },
    doDeleteSubscription() {
      var self = this;
      self.deletingSubscription = true;
      self.subscriptionApi
        .delete(self.subscriptionToDelete.id)
        .then(() => {
          self.$toast.success("Subscription deleted");
          self.deleteSubscriptionModal = false;
          self.subscriptionToDelete = {};
          self.resetObj();
        })
        .catch(({ data }) => {
          self.$toast.error("Couldn't delete the subscription. " + helper.getErrorMessage(data));
        })
        .finally(() => {
          self.deletingSubscription = false;
        });
    },
    resetObj() {
      var self = this;
      self.loadError = false;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            const result = response.result || {};
            if (!result.tenantAdmin) result.tenantAdmin = { fullName: "", email: "" };
            self.obj = result;
          })
          .catch((error) => {
            console.error(error);
            self.loadError = true;
          });
      } else {
        self.obj = self.getEmptyObj();
        self.isResetPassword = true;
      }
    },

    submit() {
      var self = this;
      if (self.saving) return;
      self.saving = true;
      if (!self.obj.id) {
        var registerDto = {
          email: self.obj.tenantAdmin.email,
          password: self.plainPassword,
          passwordConfirmation: self.passwordConfirmation,
          isSendEmailVerification: false,
        };
        auth
          .register(registerDto)
          .then((response) => {
            var tenantId = response.userData.profile.tenantId;
            self.$toast.success("Tenant added");
            self.$router.push({ path: `/admins/tenant/${tenantId}` });
          })
          .catch((error) => {
            console.error(error);
            self.$toast.error(
              "Couldn't add the tenant. " + helper.getErrorMessage(error && error.data)
            );
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        self.api
          .update(self.obj)
          .then(() => {
            self.$toast.success("Tenant saved");
          })
          .catch(({ data }) => {
            self.$toast.error("Couldn't save the tenant. " + helper.getErrorMessage(data));
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    getEmptyObj() {
      return {
        name: "",
        tenantAdmin: {
          fullName: "",
          email: "",
        },
        subscriptions: [],
      };
    },
  },
};
</script>
